import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { api, type Crossword as Puzzle, type CrosswordEntry } from '../api'
import { radius, theme, TAP_MIN } from '../theme'
import { Button, Card, Page } from '../components/ui'
import { ErrorScreen, Loading, Empty } from '../components/States'
import { LetterKeyboard } from '../components/LetterKeyboard'
import { Mascot } from '../components/Mascot'
import { Trace } from '../lib/trace'
import { say } from '../lib/speech'
import { sfx } from '../lib/sound'
import { useViewport } from '../lib/screen'
import { BackRow } from './MysteryWord'

/**
 * A mini crossword made from the child's own words. The clues are the
 * meanings the app already has, so every clue is a retrieval: here is what
 * it means, now find the word. research/word-games.md ranked this first.
 *
 * Mini on purpose: four to six words, built fresh each time on the server
 * (repo/games.py), so it fits a phone and is done in a few minutes.
 *
 * **Help is free.** "Show a letter" and "Check" cost nothing, because there
 * is nothing to lose: no score, no timer, no streak (D-049). A child stuck on
 * a word is better off seeing one letter than giving up.
 *
 * Nothing is saved (D-063). It cannot grow the garden.
 */

type Dir = 'across' | 'down'

function cellsOf(e: CrosswordEntry): [number, number][] {
  return Array.from({ length: e.length }, (_, k) =>
    (e.direction === 'across' ? [e.row, e.col + k] : [e.row + k, e.col]) as [number, number])
}

export function Crossword({
  learnerId, onBack, onHome,
}: { learnerId: string; onBack: () => void; onHome: () => void }) {
  const [puzzle, setPuzzle] = useState<Puzzle | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [letters, setLetters] = useState<string[][]>([])
  const [hinted, setHinted] = useState<Set<string>>(new Set())
  const [checked, setChecked] = useState(false)
  const [sel, setSel] = useState<{ r: number; c: number; dir: Dir } | null>(null)
  const solvedBefore = useRef<Set<number>>(new Set())
  const { width } = useViewport()

  const load = useCallback(() => {
    setError(null)
    setPuzzle(null)
    api.crossword(learnerId).then((p) => {
      setPuzzle(p)
      if (p.ready) {
        setLetters(p.solution.map((row) => [...row].map(() => '')))
        setHinted(new Set())
        setChecked(false)
        solvedBefore.current = new Set()
        const first = p.entries[0]!
        setSel({ r: first.row, c: first.col, dir: first.direction })
      }
    }).catch(setError)
  }, [learnerId])
  useEffect(load, [load])

  const ready = puzzle && puzzle.ready ? puzzle : null

  // Which entries pass through each cell.
  const at = useMemo(() => {
    const m = new Map<string, Partial<Record<Dir, number>>>()
    ready?.entries.forEach((e, i) => {
      for (const [r, c] of cellsOf(e)) {
        const k = `${r},${c}`
        m.set(k, { ...m.get(k), [e.direction]: i })
      }
    })
    return m
  }, [ready])

  const numbers = useMemo(() => {
    const m = new Map<string, number>()
    ready?.entries.forEach((e) => m.set(`${e.row},${e.col}`, e.number))
    return m
  }, [ready])

  const solved = useMemo(() => {
    const s = new Set<number>()
    ready?.entries.forEach((e, i) => {
      if (cellsOf(e).every(([r, c], k) => letters[r]?.[c] === e.answer[k])) s.add(i)
    })
    return s
  }, [ready, letters])

  const done = Boolean(ready && solved.size === ready.entries.length)

  // A word just completed: chime and say it. The whole grid: the finish.
  useEffect(() => {
    if (!ready) return
    const fresh = [...solved].filter((i) => !solvedBefore.current.has(i))
    solvedBefore.current = new Set(solved)
    if (!fresh.length) return
    if (solved.size === ready.entries.length) {
      sfx.correct()
      window.setTimeout(() => sfx.finished(), 450)
    } else {
      sfx.correct()
      say(ready.entries[fresh[0]!]!.word)
    }
  }, [solved, ready])

  const current = useMemo(() => {
    if (!ready || !sel) return null
    const here = at.get(`${sel.r},${sel.c}`)
    const i = here?.[sel.dir] ?? here?.[sel.dir === 'across' ? 'down' : 'across']
    return i === undefined ? null : i
  }, [ready, sel, at])

  const entry = ready && current !== null ? ready.entries[current]! : null
  const inCurrent = useMemo(
    () => new Set(entry ? cellsOf(entry).map(([r, c]) => `${r},${c}`) : []), [entry])

  const write = useCallback((r: number, c: number, value: string) => {
    setChecked(false)
    setLetters((rows) => rows.map((row, i) => (i === r ? row.map((v, j) => (j === c ? value : v)) : row)))
  }, [])

  const onLetter = useCallback((l: string) => {
    if (!entry || !sel || done) return
    write(sel.r, sel.c, l)
    const cells = cellsOf(entry)
    const k = cells.findIndex(([r, c]) => r === sel.r && c === sel.c)
    const next = cells[k + 1]
    if (next) setSel({ r: next[0], c: next[1], dir: entry.direction })
  }, [entry, sel, done, write])

  const onBackKey = useCallback(() => {
    if (!entry || !sel || done) return
    if (letters[sel.r]?.[sel.c]) { write(sel.r, sel.c, ''); return }
    const cells = cellsOf(entry)
    const k = cells.findIndex(([r, c]) => r === sel.r && c === sel.c)
    const prev = cells[k - 1]
    if (prev) {
      write(prev[0], prev[1], '')
      setSel({ r: prev[0], c: prev[1], dir: entry.direction })
    }
  }, [entry, sel, done, letters, write])

  const tapCell = (r: number, c: number) => {
    const here = at.get(`${r},${c}`)
    if (!here) return
    sfx.tap()
    if (sel && sel.r === r && sel.c === c && here.across !== undefined && here.down !== undefined) {
      setSel({ r, c, dir: sel.dir === 'across' ? 'down' : 'across' })
      return
    }
    const dir: Dir = sel && here[sel.dir] !== undefined ? sel.dir : here.across !== undefined ? 'across' : 'down'
    setSel({ r, c, dir })
  }

  const pickEntry = (i: number) => {
    if (!ready) return
    const e = ready.entries[i]!
    // Land on the first empty square of the word, or its start.
    const cells = cellsOf(e)
    const empty = cells.find(([r, c]) => !letters[r]?.[c]) ?? cells[0]!
    sfx.tap()
    setSel({ r: empty[0], c: empty[1], dir: e.direction })
  }

  const step = (by: number) => {
    if (!ready || current === null) return
    const n = ready.entries.length
    pickEntry((current + by + n) % n)
  }

  const showLetter = () => {
    if (!ready || !sel) return
    const right = ready.solution[sel.r]![sel.c]!
    write(sel.r, sel.c, right)
    setHinted((h) => new Set(h).add(`${sel.r},${sel.c}`))
  }

  if (error) return <ErrorScreen error={error} onRetry={load} onHome={onHome} />
  if (!puzzle) return <Loading what="Building your crossword" />
  if (!ready) {
    return (
      <Page width={560}>
        <BackRow onBack={onBack} />
        <Empty emoji="🧩" title="Not yet" line={(puzzle as { message: string }).message} />
      </Page>
    )
  }

  const avail = Math.min(560, width - 32)
  const size = Math.max(26, Math.min(46, Math.floor((avail - (ready.cols - 1) * 3) / ready.cols)))

  return (
    <Page width={560}>
      <BackRow onBack={onBack} />
      <h1 style={{ margin: '4px 0 2px', fontSize: 28, color: theme.text }}>Mini crossword</h1>
      <p style={{ margin: '0 0 12px', fontSize: 16, color: theme.muted }}>
        Every answer is one of your words. Tap a square, then type.
      </p>

      {/* the grid */}
      <div style={{ display: 'grid', placeItems: 'center', marginBottom: 12 }}>
        <div role="grid" aria-label="Crossword" style={{
          display: 'grid', gap: 3,
          gridTemplateColumns: `repeat(${ready.cols}, ${size}px)`,
          gridAutoRows: `${size}px`,
        }}>
          {ready.solution.flatMap((row, r) => [...row].map((ch, c) => {
            const k = `${r},${c}`
            if (ch === '.') return <div key={k} aria-hidden />
            const value = letters[r]?.[c] ?? ''
            const isSel = sel?.r === r && sel?.c === c
            const wrong = checked && value && value !== ch
            const inSolved = [at.get(k)?.across, at.get(k)?.down].some((i) => i !== undefined && solved.has(i))
            const bg = isSel ? '#FFE08A' : inCurrent.has(k) ? '#FFF4CC' : inSolved ? theme.greenPale : '#FFFFFF'
            return (
              <button
                key={k}
                type="button"
                role="gridcell"
                onClick={() => tapCell(r, c)}
                aria-label={`${numbers.get(k) ? `${numbers.get(k)}, ` : ''}${value || 'empty'}`}
                style={{
                  position: 'relative', padding: 0, borderRadius: 5,
                  border: `2px solid ${isSel ? theme.orange : theme.text2}`,
                  background: bg, cursor: 'pointer', touchAction: 'manipulation',
                  fontFamily: 'inherit', fontWeight: 800, fontSize: Math.round(size * 0.52),
                  color: wrong ? theme.red : hinted.has(k) ? theme.blue : inSolved ? theme.greenDarker : theme.text,
                  textDecoration: wrong ? 'line-through' : 'none',
                }}
              >
                {numbers.get(k) ? (
                  <span style={{
                    position: 'absolute', top: 1, left: 3, fontSize: Math.max(9, Math.round(size * 0.24)),
                    fontWeight: 700, color: theme.muted, lineHeight: 1,
                  }}>{numbers.get(k)}</span>
                ) : null}
                {value}
              </button>
            )
          }))}
        </div>
      </div>

      {done ? (
        <Card style={{ textAlign: 'center', marginBottom: 14 }}>
          <div style={{ display: 'grid', placeItems: 'center', marginBottom: 4 }}>
            <Mascot size={84} mood="cheer" />
          </div>
          <div style={{ fontSize: 22, fontWeight: 800, color: theme.greenDarker, marginBottom: 10 }}>
            All done!
          </div>
          <div style={{ textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
            {ready.entries.map((e) => (
              <button key={`${e.number}${e.direction}`} type="button" onClick={() => say(e.word)} style={{
                textAlign: 'left', background: 'transparent', border: 'none', cursor: 'pointer',
                fontFamily: 'inherit', fontSize: 15, color: theme.text2, lineHeight: 1.4, padding: 0,
              }}>
                <strong style={{ color: theme.text }}><Trace id="T3.display_form">{e.word}</Trace></strong> 🔊{' '}
                — <Trace id="T4.definition">{e.clue}</Trace>
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button tone="child" onClick={load}>New crossword</Button>
            <Button tone="ghost" onClick={onBack}>Other games</Button>
          </div>
        </Card>
      ) : (
        <>
          {/* the clue being worked on */}
          {entry ? (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6, background: '#FFF4CC',
              borderRadius: radius.lg, padding: '8px 6px', marginBottom: 10, minHeight: 64,
            }}>
              <button type="button" onClick={() => step(-1)} aria-label="Previous clue" style={arrow()}>‹</button>
              <div style={{ flex: 1, fontSize: 16, lineHeight: 1.4, color: theme.text }}>
                <strong>{entry.number} {entry.direction === 'across' ? 'Across' : 'Down'}</strong>
                <span style={{ color: theme.muted }}> · {entry.length} letters</span>
                <br />
                <Trace id="T4.definition">{entry.clue}</Trace>{' '}
                <button type="button" onClick={() => say(entry.clue)} aria-label="Read the clue out loud"
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 16 }}>🔊</button>
              </div>
              <button type="button" onClick={() => step(1)} aria-label="Next clue" style={arrow()}>›</button>
            </div>
          ) : null}

          <LetterKeyboard onLetter={onLetter} onBack={onBackKey} />

          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', margin: '12px 0 18px', flexWrap: 'wrap' }}>
            <Button tone="quiet" onClick={showLetter}>💡 Show a letter</Button>
            <Button tone="quiet" onClick={() => { sfx.tap(); setChecked(true) }}>✔ Check</Button>
          </div>

          {/* every clue, so a child can pick where to start */}
          {(['across', 'down'] as Dir[]).map((d) => {
            const list = ready.entries.map((e, i) => ({ e, i })).filter(({ e }) => e.direction === d)
            if (!list.length) return null
            return (
              <div key={d} style={{ marginBottom: 12 }}>
                <div style={{ fontWeight: 800, color: theme.text, marginBottom: 4 }}>
                  {d === 'across' ? 'Across' : 'Down'}
                </div>
                {list.map(({ e, i }) => (
                  <button key={i} type="button" onClick={() => pickEntry(i)} style={{
                    display: 'block', width: '100%', textAlign: 'left', fontFamily: 'inherit',
                    fontSize: 15, lineHeight: 1.4, padding: '6px 8px', borderRadius: radius.sm,
                    border: 'none', cursor: 'pointer', minHeight: 36,
                    background: current === i ? '#FFF4CC' : 'transparent',
                    color: solved.has(i) ? theme.greenDark : theme.text2,
                    textDecoration: solved.has(i) ? 'line-through' : 'none',
                  }}>
                    <strong>{e.number}.</strong> {e.clue} {solved.has(i) ? '✓' : ''}
                  </button>
                ))}
              </div>
            )
          })}
        </>
      )}
    </Page>
  )
}

// A function, not a constant: `theme` follows the child's colours, and a
// constant would freeze whichever palette was on when the file loaded.
const arrow = () => ({
  width: 40, minHeight: TAP_MIN, border: 'none', background: 'transparent',
  fontSize: 30, fontWeight: 700, color: theme.text2, cursor: 'pointer', padding: 0,
}) as const
